// A form's draft read back against the form as it opens (docs/plans/dialog-safety.md): each
// saved value is taken only where it is the same kind of value as the form's own, so a draft
// from an older app, or one that was tampered with, never puts a string where a number goes.
// Fields the form has now and the draft does not keep the form's value. Pure, unit-tested.

export type Kind = 'null' | 'array' | 'object' | 'string' | 'number' | 'boolean' | 'other'

function kindOf(v: unknown): Kind {
  if (v === null || v === undefined) return 'null'
  if (Array.isArray(v)) return 'array'
  const t = typeof v
  return t === 'object' || t === 'string' || t === 'number' || t === 'boolean' ? t : 'other'
}

/** Optional values (a price, a photo, a diet mark) may be null on either side. */
const NULLABLE: readonly Kind[] = ['string', 'number', 'object']

function fits(base: unknown, saved: unknown, hint: Kind | undefined): boolean {
  const b = kindOf(saved)
  if (b === 'other') return false
  if (b === 'number' && !Number.isFinite(saved)) return false
  // A field that starts empty says what it holds; without that it takes nothing.
  const a = kindOf(base) === 'null' ? hint : kindOf(base)
  if (!a) return false
  return a === b || (b === 'null' && NULLABLE.includes(a))
}

/**
 * `base` with every field of `saved` that fits it, and how many fields were taken. `nullable`
 * names the kind of each field whose value may start as null (a price not typed yet).
 */
export function mergeFormDraft<T extends Record<string, unknown>>(
  base: T,
  saved: unknown,
  nullable: Partial<Record<keyof T, Kind>> = {},
): { values: T; taken: number } {
  const values = { ...base }
  let taken = 0
  if (!saved || typeof saved !== 'object' || Array.isArray(saved)) return { values, taken }
  for (const key of Object.keys(base) as (keyof T & string)[]) {
    if (!(key in saved)) continue
    const value = (saved as Record<string, unknown>)[key]
    if (!fits(base[key], value, nullable[key])) continue
    values[key] = value as T[typeof key]
    taken++
  }
  return { values, taken }
}

/** The draft as stored: an object, or null when it is not one. */
export function parseFormDraft(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null
}
